import { cacheFactory } from '@trello/quickload';
import { sendErrorEvent } from '@trello/error-reporting';
import type { FieldNode } from 'graphql';
import type { ResolverContext, RestResourceResolverArgs } from '../types';
import { Analytics, getScreenFromUrl } from '@trello/atlassian-analytics';
import { getValidPreloadsForRoute } from './getValidPreloadsForRoute';
import { fullDocumentToFieldDocument } from './fullDocumentToFieldDocument';
import { WaitOnQuickLoads } from '../generated';

/**
 * Will wait on quickload queries to be synced to the apollo cache,
 * and once they are are will check cache for results and return the
 * result or null. Returning null signifies that the consumer likely has
 * implemented a query that does not satisfy the quickload constrait. For example,
 * using waitOn: ['MemberHeader'] signifies that the consumer expects the result to
 * come from cache once MemberHeader is synced.
 * @param context
 * @param field
 * @param rootArgs
 * @returns either a result from the cache or null
 */
export const waitOnQuickLoad = async (
  context: ResolverContext,
  field: FieldNode,
  rootArgs: RestResourceResolverArgs,
) => {
  try {
    const queriesToWaitOn = (rootArgs?.waitOn || []) as Array<WaitOnQuickLoads>;

    // explicitly stated that we don't want to wait on anything, return early
    if (queriesToWaitOn.length === 1 && queriesToWaitOn[0] === 'None') {
      return null;
    }

    const validPreloadsForRoute = getValidPreloadsForRoute();

    await Promise.all(
      queriesToWaitOn
        .filter((queryName) => {
          if (validPreloadsForRoute.includes(queryName)) {
            return true;
          }

          if (process.env.NODE_ENV !== 'production') {
            console.warn(
              `${context.operationName} attempted to wait on ${queryName} but ${queryName} is not a valid preload on this route.`,
            );
          } else {
            Analytics.sendOperationalEvent({
              action: 'failed',
              actionSubject: 'quickloadDirective',
              attributes: {
                operation: context.operationName,
                query: queryName,
              },
              source: getScreenFromUrl(),
            });
          }

          // fallback to making the query essentially
          return false;
        })
        .map((query) => cacheFactory.waitForQueryHydratedTo(query, 'Apollo')),
    );

    const result = context.client.readQuery({
      query: fullDocumentToFieldDocument(context.document, field, rootArgs),
      variables: rootArgs,
    });

    const rootFieldModel = field.name.value;
    // take { member: {...} } and extract to {...}
    if (result && result[rootFieldModel]) {
      return result[rootFieldModel];
    }

    return null;
  } catch (err) {
    sendErrorEvent(err instanceof Error ? err : new Error(err as string), {
      tags: {
        ownershipArea: 'trello-fe-platform',
      },
      extraData: {
        component: 'restResourceResolver',
      },
    });

    return null;
  }
};
